/**
 * Zone 1: Situation Summary
 *
 * Top banner — immediate understanding in <5 seconds.
 * Shows: severity, headline, short explanation.
 * Always visible, never scrolled away.
 */

import type { SituationSummary } from '../../services/investigationEngine';

interface SituationBannerProps {
  situation: SituationSummary;
}

const SEVERITY_STYLES: Record<string, { bg: string; border: string; icon: string; text: string }> = {
  critical: {
    bg: 'bg-rose-950/60',
    border: 'border-rose-700/60',
    icon: '🔴',
    text: 'text-rose-200',
  },
  warning: {
    bg: 'bg-amber-950/40',
    border: 'border-amber-700/50',
    icon: '🟡',
    text: 'text-amber-200',
  },
  info: {
    bg: 'bg-slate-800/60',
    border: 'border-slate-600/50',
    icon: '🟢',
    text: 'text-slate-300',
  },
};

export function SituationBanner({ situation }: SituationBannerProps) {
  const style = SEVERITY_STYLES[situation.severity] ?? SEVERITY_STYLES.info!;
  const ariaRole = situation.severity === 'critical' ? 'alert' : 'status';

  return (
    <div role={ariaRole} className={`${style.bg} border ${style.border} rounded-lg px-5 py-4`}>
      <div className="flex items-start gap-3">
        <span className="text-lg mt-0.5 flex-shrink-0">{style.icon}</span>
        <div className="min-w-0">
          <h2 className={`text-sm font-semibold ${style.text} leading-tight`}>
            {situation.headline}
          </h2>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            {situation.explanation}
          </p>
        </div>
      </div>
    </div>
  );
}
